import { uniq } from "es-toolkit";
import type { ReactElement, ReactNode } from "react";
import { fmt } from "../App/fmt.ts";
import { FeatureEnum } from "../fmt.ts";
import { vars } from "../vars.css.ts";
import { keyFacts } from "./ShopPage.css.ts";

export type KeyFact =
  | { type: "IMMEDIATE_DOWNLOAD" }
  | { type: "UNLOCKS"; features: string[] }
  | { type: "EARLY_ACCESS" };

function DownloadIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill={vars.color.blue}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M7 20.9811C3.64378 20.7257 1 17.9216 1 14.5C1 12.1716 2.22429 10.1291 4.06426 8.9812C4.56469 5.044 7.92686 2 12 2C16.0731 2 19.4353 5.044 19.9357 8.9812C21.7757 10.1291 23 12.1716 23 14.5C23 17.9216 20.3562 20.7257 17 20.9811V21H7V20.9811ZM13 12V8H11V12H8L12 17L16 12H13Z" />
    </svg>
  );
}

function UnlockIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="#2DB341"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M7 10H20C20.5523 10 21 10.4477 21 11V21C21 21.5523 20.5523 22 20 22H4C3.44772 22 3 21.5523 3 21V11C3 10.4477 3.44772 10 4 10H5V9C5 5.13401 8.13401 2 12 2C14.7405 2 17.1131 3.5748 18.2624 5.86882L16.4731 6.76344C15.6522 5.12486 13.9575 4 12 4C9.23858 4 7 6.23858 7 9V10ZM10 15V17H14V15H10Z" />
    </svg>
  );
}

function EarlyAccessIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="#FFC14D"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12ZM20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12ZM17.9998 12C17.9998 15.3137 15.3135 18 11.9998 18C8.68606 18 5.99977 15.3137 5.99977 12H11.9998V6C15.3135 6 17.9998 8.68629 17.9998 12Z" />
    </svg>
  );
}

function KeyFactRow(props: { icon: ReactNode; children: ReactNode }) {
  return (
    <li className={keyFacts.item}>
      <div>{props.icon}</div>
      <div>{props.children}</div>
    </li>
  );
}

export function KeyFactsList(props: {
  facts: KeyFact[];
  productsCount: number;
}) {
  const { facts, productsCount } = props;

  if (!facts.length) {
    return null;
  }

  return (
    <div className={keyFacts.container}>
      <p className={keyFacts.intro}>Good to know</p>

      <ul>
        {facts.map((fact): ReactElement => {
          switch (fact.type) {
            case "IMMEDIATE_DOWNLOAD": {
              const downloads = fmt.plural(productsCount, {
                one: "digital download",
                other: "digital downloads",
              });
              return (
                <KeyFactRow key={fact.type} icon={<DownloadIcon />}>
                  {"Your "}
                  <strong>{downloads}</strong>
                  {" will be available immediately after purchase."}
                </KeyFactRow>
              );
            }

            case "UNLOCKS": {
              const features = fact.features
                .filter((f) => FeatureEnum.has(f))
                .map((f) => FeatureEnum.get(f));

              const list = fmt.conjunction(uniq(features));
              const products = fmt.plural(productsCount, {
                one: "this product",
                other: "some of these products",
              });
              return (
                <KeyFactRow key={fact.type} icon={<UnlockIcon />}>
                  {"Owning "}
                  {products}
                  {" unlocks "}
                  <strong>{list}</strong> references in the companion app.
                </KeyFactRow>
              );
            }

            case "EARLY_ACCESS": {
              return (
                <KeyFactRow key={fact.type} icon={<EarlyAccessIcon />}>
                  {fmt.plural(productsCount, {
                    one: "This product is",
                    other: "Some of these products are",
                  })}{" "}
                  in <strong>early access</strong> and{" "}
                  {fmt.plural(productsCount, { one: "its", other: "their" })}{" "}
                  content might change. All future updates included.
                </KeyFactRow>
              );
            }
          }
        })}
      </ul>
    </div>
  );
}
